import React, { useState, useEffect } from 'react';
import { FaBars, FaTimes, FaHeart, FaHome, FaChevronLeft, FaComment, FaEllipsisH, FaTrash } from 'react-icons/fa';
import { IoIosSpeedometer } from "react-icons/io";
import { MdOutlineChat, MdOutlineFavoriteBorder, MdOutlineHome, MdSpeed } from "react-icons/md"; // Switched to line-based icons
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './Sidebar.css';

const Sidebar = ({ 
  menuOpen, 
  toggleMenu, 
  user, 
  setMenuOpen,
  // Chat props
  currentChatId,
  onChatSelect,
  onNewChat,
  isPracticePage = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [chats, setChats] = useState([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadingRef, setLoadingRef] = useState(false); // Prevent concurrent loads
  const [menuOpenFor, setMenuOpenFor] = useState(null); // Track which chat menu is open

  const API_BASE = process.env.REACT_APP_API_BASE || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://studyvarious.com/api');

  useEffect(() => {
    if (user && user.id) {
      setHasLoaded(false); // Reset loading state when user context changes
    }
  }, [user]);

  useEffect(() => {
    if ((menuOpen || isPracticePage) && user && user.id && !hasLoaded) {
      loadChats();
    }
  }, [menuOpen, isPracticePage, user, hasLoaded]);

  useEffect(() => {
    // Auto-select the most recent chat if no chat is selected and chats are loaded
    if (isPracticePage && !currentChatId && chats.length > 0 && onChatSelect) {
      onChatSelect(chats[0].chat_id);
    }
  }, [isPracticePage, chats, currentChatId, onChatSelect]);

  useEffect(() => {
    // Auto-create a new chat if on practice page, no current chat, chats loaded but empty
    if (isPracticePage && !currentChatId && hasLoaded && chats.length === 0 && user && user.id && onNewChat) {
      handleNewChatClick();
    }
  }, [isPracticePage, currentChatId, hasLoaded, chats.length, user, onNewChat]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpenFor && !event.target.closest('.chat-menu-container')) {
        setMenuOpenFor(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpenFor]);

  // Close menu when sidebar closes
  useEffect(() => {
    if (!menuOpen) {
      setMenuOpenFor(null);
    }
  }, [menuOpen]);

  const loadChats = async () => {
    if (!user || !user.id || loadingRef) return; // Prevent concurrent loads
    
    setLoadingRef(true);
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/chats`, { 
        params: { user_id: user.id },
        timeout: 10000 // 10 second timeout
      });
      console.log('Loaded chats:', response.data.length, response.data);
      setChats(response.data);
    } catch (error) {
      console.error('Error loading chats:', error);
      // If there's an error, still set hasLoaded to true so we don't get stuck
      setChats([]); // Ensure chats is empty on error
    } finally {
      setLoading(false);
      setHasLoaded(true);
      setLoadingRef(false);
    }
  };

  const handleNewChatClick = async () => {
    if (!user || !user.id || !onNewChat) return;
    
    try {
      const response = await axios.post(`${API_BASE}/chats`, {
        user_id: user.id,
        title: `Chat ${new Date().toLocaleDateString()}`
      }, {
        timeout: 10000
      });

      const newChat = response.data;

      // Prepend new chat to the list for immediate visibility
      setChats(prev => [newChat, ...prev]);
      setHasLoaded(true);

      // Navigate to practice page if not already there
      if (!isPracticePage) {
        navigate('/practice');
      }
      
      onNewChat(newChat.chat_id);
      setMenuOpen(false);
    } catch (error) {
      console.error('Error creating new chat:', error);
    }
  };

  const handleDeleteChat = async (chatId, event) => {
    event.stopPropagation(); // Prevent triggering chat selection
    
    if (!user || !user.id) return;
    
    try {
      await axios.delete(`${API_BASE}/chats/${chatId}`, { 
        params: { user_id: user.id } 
      });
      
      // Remove from local state
      setChats(prev => prev.filter(chat => chat.chat_id !== chatId));
      
      // If the deleted chat was currently selected, clear selection
      if (chatId === currentChatId && onChatSelect) {
        onChatSelect(null);
      }
      
      setMenuOpenFor(null); // Close the menu
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };
  
  const handleTuner = () => {
    navigate('/tuner');
    setMenuOpen(false);
  };

  const handleHome = () => {
    navigate('/');
    setMenuOpen(false);
    // Scroll to hero section after navigation
    setTimeout(() => {
      const heroElement = document.querySelector('.hero-card');
      if (heroElement) {
        heroElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className={`mobile-menu-sidebar ${menuOpen ? 'open' : ''}`}>
      <button className="hamburger-button" onClick={toggleMenu} aria-label="Toggle menu">
        {menuOpen ? <FaChevronLeft /> : <FaBars />}
      </button>

      <div className="sidebar-content">
        {user && (
          <div className="mobile-user">
            <img src={user.picture} alt={user.name} className="mobile-avatar" />
            <div className="mobile-username">{user.name}</div>
          </div>
        )}

        <button className={`menu-item ${isActive('/') ? 'selected' : ''}`} onClick={handleHome}>
          <MdOutlineHome size={24} /> <span className="menu-label">Home</span>
        </button>

        <button className={`menu-item ${isActive('/favorites') ? 'selected' : ''}`} onClick={() => { navigate('/favorites'); setMenuOpen(false); }}>
          <MdOutlineFavoriteBorder size={24} /> <span className="menu-label">Favorites</span>
        </button>

        <button className={`menu-item ${isActive('/tuner') ? 'selected' : ''}`} onClick={handleTuner}>
          <MdSpeed size={24} /> <span className="menu-label">Tuner</span>
        </button>

        <button className="menu-item" onClick={handleNewChatClick}>
          <MdOutlineChat size={24} /> <span className="menu-label">New Chat</span>
        </button>

        {/* Previous Chats - only show when sidebar is expanded */}
        {menuOpen && (
          <>
            <div className="sidebar-divider"></div>
            
            <div className="chat-list-section">
              <div 
                className="chat-list-header"
                onClick={() => {
                  setIsExpanded(!isExpanded);
                  // Load chats if not loaded yet
                  if (!isExpanded && chats.length === 0 && user && user.id) {
                    loadChats();
                  }
                }}
              >
                <span className="chat-list-title">Previous Chats</span>
                <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
              </div>
              
              {isExpanded && (
                <div className="chat-list">
                  {loading ? (
                    <div className="loading">Loading chats...</div>
                  ) : !hasLoaded ? (
                    <div className="loading">Loading chats...</div>
                  ) : chats.length === 0 ? (
                    <div className="no-chats">No previous chats</div>
                  ) : (
                    chats.map(chat => (
                      <div 
                        key={chat.chat_id}
                        className={`chat-item ${chat.chat_id === currentChatId ? 'active' : ''}`}
                      >
                        <div 
                          className="chat-item-content"
                          onClick={() => onChatSelect && onChatSelect(chat.chat_id)}
                        >
                          <div className="chat-title">{chat.title}</div>
                          <div className="chat-date">{formatDate(chat.last_updated || chat.created_at)}</div>
                        </div>
                        <div className="chat-menu-container">
                          <button 
                            className="chat-menu-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuOpenFor(menuOpenFor === chat.chat_id ? null : chat.chat_id);
                            }}
                          >
                            <FaEllipsisH />
                          </button>
                          {menuOpenFor === chat.chat_id && (
                            <div className="chat-menu">
                              <button 
                                className="chat-menu-item delete"
                                onClick={(e) => handleDeleteChat(chat.chat_id, e)}
                              >
                                <FaTrash /> Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Sidebar;